import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Benutzer} from "../../api";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";

export type BenutzerFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<BenutzerFilter>, "filter"> &
    Partial<Pick<DataRequest<BenutzerFilter>, "filter">>;

const tableName: string = "benutzer"

export default function BenutzerDataTable({...input}: Props) {
    const request = useDataRequest<BenutzerFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <BenutzerResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<BenutzerFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<BenutzerFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-benutzer");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-benutzer");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function BenutzerResults({
                             request,
                             setFilteredState,
                         }: {
    request: DataRequestState<BenutzerFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<BenutzerFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadBenutzer: DataLoader<BenutzerFilter, Benutzer> = useCallback(
        async (params) => (await getApi()).benutzer.sucheBenutzer({...params}),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadBenutzer}
            onRowClick={(row) => {
                navigate("/benutzer/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Benutzer>> {
    const {t: translation} = useT("customer");

    return useMemo(
        () => [
            {
                field: "personalNummer",
                headerName: capitalize(translation("number")),
                flex: 1,
            },
            {
                field: "name",
                headerName: capitalize(translation("name")),
                flex: 1,
            },
            {
                field: "vorname",
                headerName: capitalize(translation("firstname")),
                flex: 1,
            },
            {
                field: "email",
                headerName: capitalize(translation("email")),
                flex: 1,
            },
            {
                field: "strasse",
                headerName: capitalize(translation("street")),
                flex: 1,
            },
            {
                field: "plz",
                headerName: capitalize(translation("plz")),
                flex: 1,
            },
            {
                field: "ort",
                headerName: capitalize(translation("city")),
                flex: 1,
            },
            {
                field: "mitarbeiterTyp",
                headerName: capitalize(translation("employee-type")),
                flex: 1,
                renderCell: params => params.value ? capitalize(params.value.toLowerCase()) : "",
            },
        ],
        [translation]
    );
}
